/**
 * Доска участников лобби (И5): список присоединившихся игроков с никами и
 * признаком `nickLocked`. Используется и ведущим, и игроком:
 *  - игрок: видит список + поле смены СВОЕГО ника (disabled, если ник зафиксирован
 *    ведущим — nickLocked);
 *  - ведущий: видит список + inline-поле переименования КАЖДОГО игрока (после
 *    хост-rename сервер выставляет игроку nickLocked).
 *
 * Фаза 15 (A2/B2): статус слота `dropped` — приглушение строки + бейдж
 * «отвалился» с tooltip времени отвала/возврата (видит только ведущий);
 * кнопка «удалить» + confirm-модал → host-команда `kick {playerId}`.
 * Игроку статусная разметка не показывается (у него свой экран реконнекта).
 *
 * Сама доска — чистый UI над `RosterEntry[]`; вся валидация/уникальность — на
 * сервере (`rename` в фазе lobby). Ошибки приходят событием `error` и показываются
 * родительским экраном.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { RosterEntry } from '../protocol';
import { DroppedBadge, useDroppedTooltip } from './DroppedBadge';
import { KickConfirmModal } from './KickConfirmModal';

export interface LobbyRosterProps {
  /** Записи доски (событие `roster`). */
  entries: RosterEntry[];
  /** ID текущего игрока — для подсветки себя (только со стороны игрока). */
  myPlayerId?: number;
  /** Игрок: callback смены своего ника. Если не задан — поле скрыто. */
  onRenameSelf?: (nick: string) => void;
  /** Игрок: ник заблокирован — поле смены ника disabled (явный override). */
  selfLocked?: boolean;
  /** Ведущий: callback смены чужого ника. Если не задан — controls скрыты. */
  onRenamePlayer?: (playerId: number, nick: string) => void;
  /**
   * Ведущий: callback удаления участника (host-команда kick, Фаза 15 B2).
   * Если не задан — кнопка «удалить» скрыта.
   */
  onKickPlayer?: (playerId: number) => void;
  /**
   * Live-режим (Фаза 15, A2): панель вне лобби — заголовок «Участники» вместо
   * «Участники лобби»; статусы отвала приходят roster_delta в любой фазе.
   */
  live?: boolean;
}

export function LobbyRoster({
  entries,
  myPlayerId,
  onRenameSelf,
  selfLocked = false,
  onRenamePlayer,
  onKickPlayer,
  live = false,
}: LobbyRosterProps): JSX.Element {
  const { t } = useTranslation();
  const selfEntry =
    myPlayerId !== undefined ? entries.find((e) => e.playerId === myPlayerId) : undefined;
  // B2: цель открытого confirm-модала удаления (null — модал закрыт).
  const [kickTarget, setKickTarget] = useState<RosterEntry | null>(null);

  return (
    <section className="roster" aria-label={t('roster.aria')}>
      <h3 className="roster-title">{live ? t('roster.liveTitle') : t('roster.title')}</h3>
      {entries.length === 0 ? (
        <p className="muted small" data-testid="roster-empty">
          {t('roster.empty')}
        </p>
      ) : (
        <ul className="roster-list" data-testid="roster-list">
          {entries.map((e) => (
            <RosterRow
              key={e.playerId}
              entry={e}
              myPlayerId={myPlayerId}
              onRenamePlayer={onRenamePlayer}
              onKickPlayer={onKickPlayer}
              onKickRequest={setKickTarget}
            />
          ))}
        </ul>
      )}
      {onRenameSelf ? (
        <SelfRenameForm
          initialNick={selfEntry?.nick ?? ''}
          locked={selfLocked || (selfEntry?.nickLocked ?? false)}
          onRename={onRenameSelf}
        />
      ) : null}
      {kickTarget !== null ? (
        <KickConfirmModal
          nick={kickTarget.nick}
          onCancel={() => setKickTarget(null)}
          onConfirm={() => {
            onKickPlayer?.(kickTarget.playerId);
            setKickTarget(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** Одна строка доски: ник + бейджи + host-controls (rename/kick, A2/B2). */
function RosterRow({
  entry,
  myPlayerId,
  onRenamePlayer,
  onKickPlayer,
  onKickRequest,
}: {
  entry: RosterEntry;
  myPlayerId?: number;
  onRenamePlayer?: (playerId: number, nick: string) => void;
  onKickPlayer?: (playerId: number) => void;
  onKickRequest: (entry: RosterEntry) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const dropped = entry.status === 'dropped';
  // A2: tooltip с историей входов без IP (приватность) — общий хук DroppedBadge
  // (15.1). Применяется только у ведущего (onKickPlayer/onRenamePlayer передаются
  // лишь в host-панелях; игроку строка не приглушается).
  const hostView = onKickPlayer !== undefined || onRenamePlayer !== undefined;
  const droppedTitle = useDroppedTooltip(entry);
  const title = hostView ? droppedTitle : undefined;

  return (
    <li
      className={dropped && hostView ? 'roster-item roster-item--dropped' : 'roster-item'}
      data-testid="roster-item"
      title={title}
    >
      <span className="roster-nick" data-testid="roster-nick">
        {entry.nick}
      </span>
      {entry.playerId === myPlayerId ? (
        <span className="roster-you" data-testid="roster-you">
          {t('roster.you')}
        </span>
      ) : null}
      {entry.nickLocked ? (
        <span className="badge roster-locked" data-testid="roster-locked">
          {t('roster.locked')}
        </span>
      ) : null}
      {dropped && hostView ? <DroppedBadge /> : null}
      {onKickPlayer ? (
        <button
          type="button"
          className="btn btn--sm btn--ghost roster-kick"
          data-testid={`roster-kick-${entry.playerId}`}
          onClick={() => onKickRequest(entry)}
        >
          {t('roster.kick.button')}
        </button>
      ) : null}
      {onRenamePlayer ? (
        <HostRenameRow playerId={entry.playerId} nick={entry.nick} onRename={onRenamePlayer} />
      ) : null}
    </li>
  );
}

/** Inline-форма переименования игрока ведущим (одна на строку доски). */
function HostRenameRow({
  playerId,
  nick,
  onRename,
}: {
  playerId: number;
  nick: string;
  onRename: (playerId: number, nick: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState(nick);
  // После успешного rename сервер шлёт обновлённый roster — синхронизируем ввод
  // с актуальным ником (иначе поле «застрянет» на старом значении).
  useEffect(() => {
    setValue(nick);
  }, [nick]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || trimmed === nick) return;
    onRename(playerId, trimmed);
  }

  return (
    <form className="roster-rename" onSubmit={submit} data-testid={`roster-rename-${playerId}`}>
      <input
        className="text-input text-input--sm"
        type="text"
        value={value}
        maxLength={30}
        onChange={(e) => setValue(e.target.value)}
        aria-label={t('rename.hostLabel')}
      />
      <button
        type="submit"
        className="btn btn--sm"
        disabled={!value.trim() || value.trim() === nick}
      >
        {t('rename.hostBtn')}
      </button>
    </form>
  );
}

/** Форма смены игроком СВОЕГО ника (disabled, если ник зафиксирован ведущим). */
function SelfRenameForm({
  initialNick,
  locked,
  onRename,
}: {
  initialNick: string;
  locked: boolean;
  onRename: (nick: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  function submit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || trimmed === initialNick) return;
    onRename(trimmed);
    setValue('');
  }

  return (
    <form className="entry entry--rename-self" onSubmit={submit} data-testid="roster-self-rename">
      <label className="field-label" htmlFor="rename-self">
        {t('rename.selfLabel')}
      </label>
      <input
        id="rename-self"
        className="text-input"
        type="text"
        maxLength={30}
        placeholder={initialNick || t('rename.selfPlaceholder')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={locked}
      />
      <button type="submit" className="btn" disabled={locked || !value.trim()}>
        {t('rename.selfBtn')}
      </button>
      {locked ? (
        <p className="muted small" data-testid="rename-self-locked">
          {t('rename.selfLocked')}
        </p>
      ) : null}
    </form>
  );
}
